export type Schema =
  | boolean
  | {
      $ref?: string;
      title?: string;
      description?: string;
      type?: string | string[];
      format?: string;
      properties?: Record<string, Schema>;
      required?: string[];
      items?: Schema;
      prefixItems?: Schema[];
      additionalProperties?: Schema;
      oneOf?: Schema[];
      anyOf?: Schema[];
      allOf?: Schema[];
      enum?: unknown[];
      const?: unknown;
      nullable?: boolean;
      deprecated?: boolean;
      readOnly?: boolean;
      writeOnly?: boolean;
      [key: string]: unknown;
    };

export interface ApiObject {
  $ref?: string;
  summary?: string;
  description?: string;
  name?: string;
  in?: string;
  required?: boolean;
  schema?: Schema;
  content?: Record<
    string,
    { schema?: Schema; example?: unknown; examples?: Record<string, ApiObject> }
  >;
  headers?: Record<string, ApiObject>;
  [key: string]: unknown;
}

export interface Operation extends ApiObject {
  tags?: string[];
  operationId?: string;
  security?: unknown[];
  deprecated?: boolean;
  parameters?: ApiObject[];
  requestBody?: ApiObject;
  responses?: Record<string, ApiObject>;
  servers?: { url: string; description?: string }[];
}

export interface SecurityScheme {
  type?: string;
  scheme?: string;
  name?: string;
  in?: string;
  description?: string;
  [key: string]: unknown;
}

export interface OpenAPI {
  openapi: string;
  info: { title: string; version: string; description?: string };
  servers?: { url: string; description?: string }[];
  tags?: { name: string; description?: string }[];
  paths: Record<string, Record<string, unknown> & { parameters?: ApiObject[] }>;
  components?: {
    schemas?: Record<string, Schema>;
    securitySchemes?: Record<string, SecurityScheme>;
    [key: string]: unknown;
  };
  security?: unknown[];
}

export const methods = [
  "get",
  "post",
  "put",
  "patch",
  "delete",
  "options",
  "head",
  "trace",
];
export const apiReferenceHref = "/docs/api-reference";
export const authenticationHref = `${apiReferenceHref}#authentication`;
export const schemaHref = (name: string) =>
  `${apiReferenceHref}/schemas/${encodeURIComponent(name)}`;
export const operationHref = (method: string, path: string) =>
  `${apiReferenceHref}/operations/${method}${path.split("/").map(encodeURIComponent).join("/")}`;
export const refName = (ref: string) =>
  decodeURIComponent(ref.split("/").at(-1) ?? ref)
    .replace(/~1/g, "/")
    .replace(/~0/g, "~");

// One hop lets schema renderers retain constraints and annotations at every
// reference in a chain, without flattening JSON Schema's conjunction semantics.
export function resolveRefOnce<T>(spec: OpenAPI, value: T): T {
  if (
    !value ||
    typeof value !== "object" ||
    !("$ref" in value) ||
    typeof value.$ref !== "string" ||
    !value.$ref.startsWith("#/")
  )
    return value;
  const target = value.$ref
    .slice(2)
    .split("/")
    .reduce<unknown>((node, key) => {
      const decoded = decodeURIComponent(key)
        .replace(/~1/g, "/")
        .replace(/~0/g, "~");
      return node && typeof node === "object"
        ? (node as Record<string, unknown>)[decoded]
        : undefined;
    }, spec);
  return target === undefined ? value : (target as T);
}

export function resolveRef<T>(spec: OpenAPI, value: T): T {
  let current: unknown = value;
  const seen = new Set<string>();
  while (
    current &&
    typeof current === "object" &&
    "$ref" in current &&
    typeof current.$ref === "string"
  ) {
    const ref = current.$ref;
    if (!ref.startsWith("#/") || seen.has(ref)) break;
    seen.add(ref);
    const target = resolveRefOnce(spec, current);
    if (target === current) break;
    current = target;
  }
  return current as T;
}

/**
 * The schemes an operation needs, from its own `security` or else the spec's.
 * Each entry is one alternative, listing schemes that are used together. An
 * empty result, or an empty alternative, means no authentication is needed.
 */
export function securityRequirements(
  spec: OpenAPI,
  operation: Pick<Operation, "security">,
): { name: string; scheme?: SecurityScheme }[][] {
  const requirements = operation.security ?? spec.security ?? [];
  return requirements.map((requirement) =>
    Object.keys(requirement ?? {}).map((name) => ({
      name,
      scheme: spec.components?.securitySchemes?.[name],
    })),
  );
}

export const capitalize = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1);

/** One line naming what an operation needs to authenticate. */
export function securitySummary(
  spec: OpenAPI,
  operation: Pick<Operation, "security">,
): string {
  const requirements = securityRequirements(spec, operation);
  if (!requirements.length || requirements.some((schemes) => !schemes.length))
    return "No authentication";
  const alternatives = requirements.map((schemes) =>
    schemes
      .map(({ name, scheme }) => {
        if (scheme?.type === "http" && /^bearer$/i.test(scheme.scheme ?? ""))
          return "a Dash0 auth token (Bearer)";
        if (scheme?.type === "http" && scheme.scheme)
          return `HTTP ${capitalize(scheme.scheme)} authentication`;
        if (scheme?.type === "apiKey" && scheme.name)
          return `the ${scheme.name} ${scheme.in ?? "header"}`;
        return name;
      })
      .join(" and "),
  );
  return `Requires ${alternatives.join(" or ")}`;
}

export function operations(spec: OpenAPI) {
  return Object.entries(spec.paths).flatMap(([path, item]) =>
    methods.flatMap((method) => {
      const operation = item[method] as Operation | undefined;
      if (!operation) return [];
      const parameters = new Map<string, ApiObject>();
      for (const parameter of [
        ...(item.parameters ?? []),
        ...(operation.parameters ?? []),
      ]) {
        const resolved = resolveRef(spec, parameter);
        parameters.set(`${resolved.in}:${resolved.name}`, resolved);
      }
      return [
        {
          ...operation,
          parameters: [...parameters.values()],
          method,
          path,
          servers:
            operation.servers ??
            (item.servers as Operation["servers"]) ??
            spec.servers,
          href: operationHref(method, path),
        },
      ];
    }),
  );
}

/**
 * Slug format of the retired readme.com site at api-docs.dash0.com. Operation
 * pages were `<method>_<slug of path>`, section pages the slug of the tag
 * name. Slugs lowercase the input, drop dots, and collapse every other run of
 * non-alphanumerics into one hyphen: "/api/prometheus/api/v1/label/{label_name}/values"
 * became "api-prometheus-api-v1-label-label-name-values", "OAuth 2.0" "oauth-20".
 */
export const legacyReferenceSlug = (value: string) =>
  value
    .toLowerCase()
    .replace(/\./g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

// Tags lose their "(BETA)" / "(Private BETA)" suffix as features graduate,
// but the old slugs kept it. Compare tag slugs without the suffix.
const withoutBetaSuffix = (slug: string) =>
  slug.replace(/-(?:private-)?beta$/, "");

/**
 * Href in this reference for a page of the retired readme.com site, given the
 * part of its path after `/reference/`. Section pages land on the first
 * operation of their tag, the same target the group links in the navigation
 * use. Returns undefined when nothing in the spec matches.
 */
export function legacyReferenceHref(
  spec: OpenAPI,
  slug: string,
): string | undefined {
  if (slug === "") return apiReferenceHref;
  const all = operations(spec);
  const operation = all.find(
    (item) => `${item.method}_${legacyReferenceSlug(item.path)}` === slug,
  );
  if (operation) return operation.href;
  const tagged = all.filter((item) => item.tags?.[0]);
  const section =
    tagged.find(
      (item) => legacyReferenceSlug(item.tags![0]) === slug,
    ) ??
    tagged.find(
      (item) =>
        withoutBetaSuffix(legacyReferenceSlug(item.tags![0])) ===
        withoutBetaSuffix(slug),
    );
  return section?.href;
}

export const apiReferenceTitle = "API reference";
export const apiReferenceDescription =
  "Dash0 API endpoints, request and response schemas, and the OpenAPI specification in JSON.";

/**
 * Resource nouns for the CRUD families that make up most of the API. The
 * <title> and description are written for search intent ("list dashboards",
 * "delete a spam filter") while the H1 stays faithful to the spec summary.
 */
const resourceNouns: Record<string, { plural: string; singular: string }> = {
  "/api/agentic-workflows": {
    plural: "Agent0 automations",
    singular: "an Agent0 automation",
  },
  "/api/alerting/check-rules": {
    plural: "check rules",
    singular: "a check rule",
  },
  "/api/dashboards": { plural: "dashboards", singular: "a dashboard" },
  "/api/members": {
    plural: "organization members",
    singular: "an organization member",
  },
  "/api/notification-channels": {
    plural: "notification channels",
    singular: "a notification channel",
  },
  "/api/operation-pattern-rules": {
    plural: "operation pattern rules",
    singular: "an operation pattern rule",
  },
  "/api/recording-rules": {
    plural: "recording rules",
    singular: "a recording rule",
  },
  "/api/sampling-rules": {
    plural: "sampling rules",
    singular: "a sampling rule",
  },
  "/api/signal-to-metrics": {
    plural: "signal-to-metrics configs",
    singular: "a signal-to-metrics config",
  },
  "/api/slos": { plural: "SLOs", singular: "an SLO" },
  "/api/spam-filters": { plural: "spam filters", singular: "a spam filter" },
  "/api/synthetic-checks": {
    plural: "synthetic checks",
    singular: "a synthetic check",
  },
  "/api/teams": { plural: "teams", singular: "a team" },
  "/api/time-series-aggregations": {
    plural: "time series aggregations",
    singular: "a time series aggregation",
  },
  "/api/views": { plural: "views", singular: "a view" },
};

// Endpoints outside the CRUD families, keyed by "METHOD /path".
const intentPhrases: Record<string, string> = {
  "POST /api/logs": "Query logs",
  "POST /api/logs/download": "Export logs to a file",
  "POST /api/spans": "Query spans",
  "POST /api/spans/download": "Export spans to a file",
  "POST /api/trace/details": "Get a trace by trace ID",
  "POST /api/trace/ids": "Find trace IDs by filter",
  "POST /api/web-events/download": "Export web events to a file",
  "POST /api/sql": "Run a SQL query",
  "GET /api/prometheus/api/v1/query": "Run an instant PromQL query",
  "POST /api/prometheus/api/v1/query": "Run an instant PromQL query",
  "GET /api/prometheus/api/v1/query_range": "Run a PromQL range query",
  "POST /api/prometheus/api/v1/query_range": "Run a PromQL range query",
  "GET /api/prometheus/api/v1/series": "List time series by label selector",
  "POST /api/prometheus/api/v1/series": "List time series by label selector",
  "GET /api/prometheus/api/v1/labels": "List metric label names",
  "POST /api/prometheus/api/v1/labels": "List metric label names",
  "GET /api/prometheus/api/v1/label/{label_name}/values":
    "List metric label values",
  "POST /api/prometheus/api/v1/label/{label_name}/values":
    "List metric label values",
  "GET /api/prometheus/api/v1/metadata": "Get metric metadata",
  "POST /api/prometheus/api/v1/metadata": "Get metric metadata",
  "GET /api/prometheus/api/v1/format_query": "Format a PromQL query",
  "POST /api/prometheus/api/v1/format_query": "Format a PromQL query",
  "GET /api/prometheus/api/v1/status/buildinfo":
    "Get Prometheus API build info",
  "GET /api/prometheus/api/v1/status/runtimeinfo":
    "Get Prometheus API runtime info",
  "POST /api/alerting/check-rules/bulk": "Bulk create check rules",
  "POST /api/import/check-rule": "Import a check rule",
  "POST /api/import/check-rules": "Import check rules in bulk",
  "POST /api/import/dashboard": "Import a dashboard",
  "PUT /api/import/signal-to-metrics": "Bulk upsert signal-to-metrics configs",
  "POST /api/import/synthetic-check": "Import a synthetic check",
  "POST /api/import/view": "Import a view",
  "POST /api/signal-to-metrics/test": "Test a signal-to-metrics config",
  "GET /api/synthetic-checks/locations": "List synthetic check locations",
  "POST /api/synthetic-checks/test": "Test a synthetic check",
  "POST /api/members": "Invite a member to the organization",
  "DELETE /api/members/{memberID}": "Remove a member from the organization",
  "PUT /api/teams/{originOrId}/display": "Update team display options",
  "POST /api/teams/{originOrId}/members": "Add members to a team",
  "DELETE /api/teams/{originOrId}/members/{memberID}":
    "Remove a member from a team",
  "GET /api/edge/settings": "Get edge collector settings",
  "GET /.well-known/oauth-authorization-server":
    "Get OAuth 2.0 authorization server metadata",
  "GET /.well-known/oauth-protected-resource":
    "Get OAuth 2.0 protected resource metadata",
  "GET /oauth/authorize": "Authorize an OAuth 2.0 client",
  "POST /oauth/register": "Register an OAuth 2.0 client",
  "POST /oauth/revoke": "Revoke an OAuth 2.0 token",
  "POST /oauth/token": "Request an OAuth 2.0 access token",
};

/**
 * Search-intent phrase for an endpoint: the explicit table first, then the
 * CRUD pattern on a known resource, then the spec summary. Never empty.
 */
export function operationIntent(operation: {
  method: string;
  path: string;
  summary?: string;
}): string {
  const method = operation.method.toUpperCase();
  const explicit = intentPhrases[`${method} ${operation.path}`];
  if (explicit) return explicit;
  const match = operation.path.match(/^(.*?)(\/\{originOrId\})?$/);
  const noun = match && resourceNouns[match[1]];
  if (noun) {
    const single = Boolean(match[2]);
    const phrase = single
      ? {
          GET: `Get ${noun.singular} by ID`,
          PUT: `Create or update ${noun.singular}`,
          PATCH: `Update ${noun.singular}`,
          DELETE: `Delete ${noun.singular}`,
        }[method]
      : { GET: `List ${noun.plural}`, POST: `Create ${noun.singular}` }[
          method
        ];
    if (phrase) return phrase;
  }
  return operation.summary?.trim() || `${method} ${operation.path}`;
}

export interface ApiReferencePage {
  href: string;
  /** Search-oriented <title>, kept short; the site layout appends " · Dash0". */
  title: string;
  description: string;
  /** What the page's H1 says. Used for social previews and breadcrumbs. */
  heading: string;
  /** Endpoint tag or "Component schema"; absent on the overview. */
  group?: string;
}

export function apiReferencePages(spec: OpenAPI): ApiReferencePage[] {
  return [
    {
      href: apiReferenceHref,
      title: apiReferenceTitle,
      description: apiReferenceDescription,
      heading: "Dash0 API reference",
    },
    ...operations(spec).map((operation) => {
      const method = operation.method.toUpperCase();
      const intent = operationIntent(operation);
      const summary = operation.summary?.trim();
      return {
        href: operation.href,
        title: `${intent} with the Dash0 API`,
        description: [
          `${intent} with the Dash0 REST API using ${method} ${operation.path}.`,
          summary && summary !== intent
            ? `${summary.replace(/\.$/, "")}.`
            : undefined,
          "Request parameters, sample curl request, and response schemas.",
        ]
          .filter(Boolean)
          .join(" "),
        heading: summary || operation.path,
        group: operation.tags?.[0],
      };
    }),
    ...Object.keys(spec.components?.schemas ?? {}).map((name) => ({
      href: schemaHref(name),
      title: `${name} (Dash0 API schema)`,
      description: `${name} in the Dash0 API. Property types, required fields, union variants, and referencing endpoints and schemas.`,
      heading: name,
      group: "Component schema",
    })),
  ];
}

const isNullType = (schema: Schema) =>
  typeof schema === "object" && schema.type === "null";

/**
 * OpenAPI 3.1 writes a nullable value as `type: [X, "null"]` and a nullable
 * reference as `anyOf: [{ $ref }, { type: "null" }]`. Returns the schema
 * without its null alternative, and whether it had one, so renderers can show
 * the member's type with a "nullable" label instead of a two-variant union.
 */
export function withoutNull(schema: Schema): {
  schema: Schema;
  nullable: boolean;
} {
  if (typeof schema === "boolean") return { schema, nullable: false };
  if (Array.isArray(schema.type) && schema.type.includes("null")) {
    const types = schema.type.filter((type) => type !== "null");
    if (types.length)
      return {
        schema: { ...schema, type: types.length === 1 ? types[0] : types },
        nullable: true,
      };
  }
  for (const kind of ["anyOf", "oneOf"] as const) {
    const variants = schema[kind];
    if (variants?.length !== 2 || !variants.some(isNullType)) continue;
    const member = variants.find((variant) => !isNullType(variant));
    if (member === undefined) continue;
    const { [kind]: _, ...siblings } = schema;
    return {
      schema:
        typeof member === "boolean"
          ? Object.keys(siblings).length
            ? siblings
            : member
          : { ...siblings, ...member },
      nullable: true,
    };
  }
  return { schema, nullable: schema.nullable === true };
}

// The one value a schema allows: a const, a one-value enum, or either inside
// an allOf member, as in `kind: { allOf: [{ $ref: Kind }, { enum: ["x"] }] }`.
function singleValue(
  spec: OpenAPI,
  input: Schema,
  depth = 0,
): { value: unknown } | undefined {
  const schema = resolveRef(spec, input);
  if (typeof schema === "boolean" || depth > 5) return;
  if (schema.const !== undefined) return { value: schema.const };
  if (schema.enum?.length === 1) return { value: schema.enum[0] };
  for (const member of schema.allOf ?? []) {
    const found = singleValue(spec, member, depth + 1);
    if (found) return found;
  }
}

// A property schema of a union variant, including from its allOf members.
function variantProperty(
  spec: OpenAPI,
  input: Schema,
  name: string,
  depth = 0,
): Schema | undefined {
  const schema = resolveRef(spec, input);
  if (typeof schema === "boolean" || depth > 5) return;
  return (
    schema.properties?.[name] ??
    (schema.allOf ?? [])
      .map((member) => variantProperty(spec, member, name, depth + 1))
      .find((property) => property !== undefined)
  );
}

function propertyNames(spec: OpenAPI, input: Schema, depth = 0): string[] {
  const schema = resolveRef(spec, input);
  if (typeof schema === "boolean" || depth > 5) return [];
  return [
    ...new Set([
      ...Object.keys(schema.properties ?? {}),
      ...(schema.allOf ?? []).flatMap((member) =>
        propertyNames(spec, member, depth + 1),
      ),
    ]),
  ];
}

/**
 * How to tell the variants of a oneOf or anyOf apart: the discriminator's
 * `propertyName` and `mapping` when present, otherwise a property that every
 * variant fixes to one value. Labels are in variant order.
 */
export function unionTags(
  spec: OpenAPI,
  schema: Schema,
): { property: string; labels: string[] } | undefined {
  if (typeof schema === "boolean") return;
  const variants = schema.oneOf ?? schema.anyOf;
  if (!variants?.length) return;
  const discriminator = schema.discriminator as
    | { propertyName?: string; mapping?: Record<string, string> }
    | undefined;
  const label = (property: string, variant: Schema) => {
    const ref = typeof variant === "object" ? variant.$ref : undefined;
    const mapped = Object.entries(discriminator?.mapping ?? {}).find(
      ([, target]) => target === ref,
    )?.[0];
    if (mapped !== undefined) return mapped;
    const tag = variantProperty(spec, variant, property);
    const fixed = tag && singleValue(spec, tag);
    if (fixed) return String(fixed.value);
    // Without a mapping, a discriminator matches the schema name.
    if (discriminator && ref) return refName(ref);
  };
  const candidates = discriminator?.propertyName
    ? [discriminator.propertyName]
    : propertyNames(spec, variants[0]);
  for (const property of candidates) {
    const labels = variants.map((variant) => label(property, variant));
    if (labels.every((value) => value !== undefined))
      return { property, labels: labels as string[] };
  }
}

/** The id the overview gives a heading in the spec's description. */
export const headingId = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/**
 * The section of the spec's description that explains the Dash0-Dataset
 * header, which the spec describes once instead of on every operation.
 */
export function datasetSelection(
  spec: OpenAPI,
): { title: string; href: string } | undefined {
  const section = (spec.info.description ?? "")
    .split(/^(?=#{1,6} )/m)
    .find((text) => /^#{1,6} /.test(text) && text.includes("Dash0-Dataset"));
  const title = section?.match(/^#{1,6} +(.+)/)?.[1].trim();
  return title
    ? { title, href: `${apiReferenceHref}#${headingId(title)}` }
    : undefined;
}

/**
 * An object whose schema says nothing about its content, such as the
 * dashboard `spec`, which embeds a Perses dashboard. Any object is valid.
 */
export const isFreeForm = (schema: Schema) =>
  typeof schema === "object" &&
  schema.type === "object" &&
  [
    "properties",
    "additionalProperties",
    "patternProperties",
    "propertyNames",
    "$ref",
    "allOf",
    "anyOf",
    "oneOf",
  ].every((key) => schema[key] === undefined);

export function schemaType(input: Schema): string {
  const schema = withoutNull(input).schema;
  if (typeof schema === "boolean") return schema ? "any value" : "never";
  if (schema.$ref) return refName(schema.$ref);
  if (schema.oneOf) return "oneOf";
  if (schema.anyOf) return "anyOf";
  if (schema.allOf) return "allOf";
  if (schema.items !== undefined) return `${schemaType(schema.items)}[]`;
  if (isFreeForm(schema)) return "object (free-form)";
  return (
    (Array.isArray(schema.type) ? schema.type.join(" | ") : schema.type) ??
    (schema.properties || schema.additionalProperties !== undefined
      ? "object"
      : schema.enum
        ? "enum"
        : "any value")
  );
}

export function references(value: unknown): Set<string> {
  const refs = new Set<string>();
  function visit(node: unknown) {
    if (!node || typeof node !== "object") return;
    for (const [key, child] of Object.entries(node)) {
      if (key === "$ref" && typeof child === "string") refs.add(child);
      else visit(child);
    }
  }
  visit(value);
  return refs;
}
